const SEON_DATA = 'tamara-seon-data'

// SEON CDN URLs in order of preference
const SEON_CDN_URLS = [
  'https://cdn.deviceinf.com/js/v6/agent.umd.js', // Primary CDN (current)
  'https://cdn.seondf.com/js/v6/agent.umd.js', // Alternative CDN
  'https://cdn.deviceinfresolver.com/js/v6/agent.umd.js', // Backup CDN
  'https://cdn.seondfresolver.com/js/v6/agent.umd.js', // Additional backup
]

// Local fallback path - hosted on our own CDN/server
const SEON_LOCAL_FALLBACK = '/scripts/seon/agent.umd.js'

// Timeout for each CDN attempt (in milliseconds)
const CDN_TIMEOUT_MS = 6000 // 7 seconds per attempt

let seonLoadAttempts = 0
let seonLoadStartTime = null
let seonScriptLoadingPromise = null
let seonScriptLoaded = false
let seonSessionCache = null
let seonSessionCacheTimestamp = null
const SEON_SESSION_CACHE_DURATION = 30 * 60 * 1000 // 30 minutes cache

const loadSeonScript = (callback, onError = null) => {
  // If script is already loaded, execute callback immediately
  if (seonScriptLoaded && window.seon) {
    if (callback) {
      callback()
    }
    return
  }

  // If script is currently loading, queue the callback
  if (seonScriptLoadingPromise) {
    seonScriptLoadingPromise
      .then(() => {
        if (callback) {
          callback()
        }
      })
      .catch((error) => {
        if (onError) {
          onError(error)
        }
      })
    return
  }

  seonLoadStartTime = Date.now()
  seonLoadAttempts = 0

  // Create a promise for this loading attempt
  seonScriptLoadingPromise = new Promise((resolve, reject) => {
    const wrappedCallback = () => {
      seonScriptLoaded = true
      seonScriptLoadingPromise = null
      if (callback) {
        callback()
      }
      resolve()
    }

    const wrappedError = (error) => {
      seonScriptLoadingPromise = null
      if (onError) {
        onError(error)
      }
      reject(error)
    }

    // Try loading from CDN URLs with fallback chain
    tryLoadSeonFromUrls(
      [...SEON_CDN_URLS, SEON_LOCAL_FALLBACK],
      wrappedCallback,
      wrappedError
    )
  })

  return seonScriptLoadingPromise
}

const tryLoadSeonFromUrls = (urls, callback, onError) => {
  if (urls.length === 0) {
    console.error('SEON SDK: All CDN sources and local fallback failed')
    if (onError) {
      onError(new Error('All SEON SDK sources failed to load'))
    }
    return
  }

  const currentUrl = urls[0]
  const remainingUrls = urls.slice(1)
  seonLoadAttempts++

  const script = document.createElement('script')
  script.src = currentUrl
  script.type = 'text/javascript'
  script.async = true
  script.crossOrigin = 'anonymous'

  let timeoutId = null
  let hasCompleted = false

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    if (script.parentNode) {
      script.parentNode.removeChild(script)
    }
  }

  const handleSuccess = () => {
    if (hasCompleted) return
    hasCompleted = true

    cleanup()

    const loadTime = Date.now() - seonLoadStartTime

    // Log successful CDN for monitoring
    if (window.newrelic) {
      window.newrelic.setCustomAttribute('seon_cdn_used', currentUrl)
      window.newrelic.setCustomAttribute('seon_load_attempts', seonLoadAttempts)
      window.newrelic.setCustomAttribute('seon_load_time_ms', loadTime)
    }

    if (callback) {
      callback()
    }
  }

  const handleFailure = (error) => {
    if (hasCompleted) return
    hasCompleted = true

    cleanup()

    console.warn(`SEON SDK: Failed to load from ${currentUrl}:`, error)

    // Try next URL in the list
    if (remainingUrls.length > 0) {
      tryLoadSeonFromUrls(remainingUrls, callback, onError)
    } else {
      // All sources failed
      const totalLoadTime = Date.now() - seonLoadStartTime
      console.error(
        `SEON SDK: All sources failed after ${seonLoadAttempts} attempts in ${totalLoadTime}ms`
      )

      // Log failure for monitoring and trigger critical alert
      if (window.newrelic) {
        window.newrelic.setCustomAttribute('seon_load_failed', true)
        window.newrelic.setCustomAttribute(
          'seon_total_attempts',
          seonLoadAttempts
        )
        window.newrelic.setCustomAttribute(
          'seon_total_load_time_ms',
          totalLoadTime
        )
        window.newrelic.setCustomAttribute('seon_all_sources_failed', true)
        window.newrelic.setCustomAttribute('alert_severity', 'critical')
        window.newrelic.setCustomAttribute(
          'alert_category',
          'fraud_detection_failure'
        )

        // Create a critical error that should trigger alerts
        const criticalError = new Error(
          'CRITICAL: SEON SDK failed to load from all sources including local backup'
        )
        criticalError.name = 'SeonLoadFailureError'
        window.newrelic.noticeError(criticalError, {
          errorType: 'seon_total_failure',
          impactLevel: 'high',
          fraudDetectionStatus: 'disabled',
          requiresImmediateAction: true,
        })

        // Additional alert trigger with custom event
        window.newrelic.addPageAction('seon_critical_failure', {
          timestamp: new Date().toISOString(),
          totalAttempts: seonLoadAttempts,
          loadTimeMs: totalLoadTime,
          userAgent: navigator.userAgent,
          pageUrl: window.location.href,
        })
      }

      if (onError) {
        onError(new Error('All SEON SDK sources failed to load'))
      }
    }
  }

  // Set up timeout
  timeoutId = setTimeout(() => {
    handleFailure(new Error(`Timeout loading from ${currentUrl}`))
  }, CDN_TIMEOUT_MS)

  // Set up event handlers
  script.onload = handleSuccess
  script.onerror = (event) => {
    handleFailure(new Error(`Script error loading from ${currentUrl}`))
  }

  // Add script to document
  document.head.appendChild(script)
}

// Enhanced version that returns a Promise for easier async/await usage
const loadSeonScriptAsync = () => {
  return new Promise((resolve, reject) => {
    loadSeonScript(resolve, reject)
  })
}

// Optimized SEON session getter with caching
const getSeonSession = async () => {
  // Check if we have a valid cached session
  const now = Date.now()
  if (
    seonSessionCache &&
    seonSessionCacheTimestamp &&
    now - seonSessionCacheTimestamp < SEON_SESSION_CACHE_DURATION
  ) {
    return seonSessionCache
  }

  // Ensure script is loaded
  await loadSeonScriptAsync()

  if (!window.seon) {
    throw new Error('SEON script failed to load')
  }

  // Initialize SEON if not already done
  if (typeof window.seon.init === 'function') {
    window.seon.init()
  }

  // Get session data
  const sessionData = await window.seon.getSession()

  // Cache the session data
  if (sessionData) {
    seonSessionCache = sessionData
    seonSessionCacheTimestamp = now
  }

  return sessionData
}

// Clear session cache (useful for testing or when session expires)
const clearSeonSessionCache = () => {
  seonSessionCache = null
  seonSessionCacheTimestamp = null
}

// Check if SEON is ready (script loaded and initialized)
const isSeonReady = () => {
  return (
    seonScriptLoaded &&
    window.seon &&
    typeof window.seon.getSession === 'function'
  )
}

// Get SEON status for debugging
const getSeonStatus = () => {
  return {
    scriptLoaded: seonScriptLoaded,
    scriptLoading: !!seonScriptLoadingPromise,
    windowSeonExists: !!window.seon,
    sessionCached: !!seonSessionCache,
    sessionCacheAge: seonSessionCacheTimestamp
      ? Date.now() - seonSessionCacheTimestamp
      : null,
  }
}

const setSeonData = (data) => {
  window.storage.setItem(SEON_DATA, JSON.stringify(data))
}

const getSeonData = () => {
  try {
    const SeonData = window.storage.getItem(SEON_DATA)

    return JSON.parse(SeonData)
  } catch (_) {
    console.error(_)
    return {}
  }
}

const clearSeonData = () => {
  window.storage.removeItem(SEON_DATA)
}

export default {
  loadSeonScript,
  loadSeonScriptAsync,
  getSeonSession,
  clearSeonSessionCache,
  isSeonReady,
  getSeonStatus,
  setSeonData,
  getSeonData,
  clearSeonData,
}
